import React from 'react';
import { ITableColumns, ITableDataSource } from 'models/table';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { IMarking, IFirearm, ISingleFRT } from 'models/firearm';
import { Chips } from 'components/atoms/Chips';
import {
  IRegisteredFirearmAttachment,
  IRegisteredFirearmAttachmentProps,
  IRegisteredFirearmLicence,
  IRegisteredFirearmTransaction,
} from 'models/registeredFirearm';
import { downloadAttachment } from 'requests/attachment';
import { isFRTLocal } from 'pages/FRT/helpers';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { getColumnTitleFromCustomField } from 'utils/table';
import { formatDate } from 'utils/date';
import { IDictionary } from 'models/dictionary';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { EPermission } from 'models/permissions';
import { Link } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { EFileType } from 'constants/FileType';
import Tooltip from 'components/atoms/Tooltip';
import Icon from 'components/atoms/Icon/Icon';
import {
  getDictionaryValue,
  getLicenceStatusChipVariant,
} from 'pages/LegalEntitiesPage/LegalEntityViewLicencePage/helpers';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import TooltipInfoCell from 'components/atoms/TooltipInfoCell';
import { EMarkingTypes } from 'constants/Markings';
interface IMarkingResults {
  [x: string]: any;
}
const prepareMarkings = (markings: IMarking[]) => {
  const result: IMarkingResults = [];
  markings.map((marking: IMarking) => {
    result[marking.markingType] = marking.marking;
  });
  return result;
};

export const mapRegisteredFirearmsToDataSource = (
  firearms: IFirearm[],
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  manufacturer: IDictionary[] | null,
  state: IDictionary[] | null,
  caliber: IDictionary[] | null,
): ITableDataSource[] =>
  firearms.map((firearm) => ({
    id: firearm.uniqueId,
    serialNumber: firearm.serialNumber,
    fireCycleId: firearm.fireCycleId,
    exhibitNumber: firearm.exhibitNumber,
    make: findInDictionary(firearm.makeUniqueId, make),
    model: findInDictionary(firearm.modelUniqueId, model),
    manufacturer: findInDictionary(firearm.manufacturerUniqueId, manufacturer),
    caliber: findInDictionary(firearm.caliberUniqueId, caliber),
    owner: firearm.owner,
    keeper: firearm.keeper,
    state: findInDictionary(firearm.stateUniqueId, state),
    legality: firearm.legality,
    legalityUniqueId: firearm.legalityUniqueId,
    activityUniqueId: firearm.activityUniqueId,
    customFields: firearm.customFields || [],
    licenceAuthorityIds: firearm.licenceAuthorityIds,
    groupName: firearm.groupName,
    showNoActiveLicenceWarning: firearm.showNoActiveLicenceWarning,
    showNoOwnerActiveLicenceWarning: firearm.showNoOwnerActiveLicenceWarning,
    showNoKeeperActiveLicenceWarning: firearm.showNoKeeperActiveLicenceWarning,
    showExpiredLinkedWarning: firearm.showExpiredLinkedWarning,
    showInactiveOwnerOrKeeperWarning: firearm.showInactiveOwnerOrKeeperWarning,
    isLatLong: firearm.latitude && firearm.longitude,
    markings: prepareMarkings(firearm.markings),
    yearOfConfiscation: firearm.yearOfConfiscation,
    yearOfTransfer: firearm.yearOfTransfer,
  }));

interface IRegisteredFirearmsTableColumns {
  handleEdit: (item: ITableDataSource) => void;
  renderStateFieldColor: (arg: ITableDataSource) => any;
  hasPermission: boolean | null;
  customFields?: ICustomFieldByObjectType[];
  t: (key: string) => string;
  isAnyLicenceAuthorityValid: (licenceAuthorityIds: string[]) => boolean;
  hasWeaponLicenceModule: boolean | null;
  showOnMap: (id: string) => void;
  markedText?: string;
}

interface IRegisteredFirearmTransactionsTableColumns {
  discardableFirearmTransactionId?: string | null;
  handleView: ((arg: string) => void) | null;
  handleDelete: ((arg: string) => void) | null;
  showDetails: (props: any) => boolean;
  longDateFormat: string;
}
interface IRegisteredFirearmLicencesTableColumns {
  handleView: (id: string, type: string) => void;
  dateFormat: string;
  weaponLicenceStatus: IDictionary[] | null;
}

interface IRegisteredFirearmAttachmentsTableColumns {
  firearmId: string;
  handleDelete: (arg: IRegisteredFirearmAttachmentProps) => void;
  showDelete?: boolean;
  handleShowTransaction: (arg: string) => void;
  longDateFormat: string;
}

export const registeredFirearmsTableColumns = (
  {
    handleEdit,
    renderStateFieldColor,
    customFields = [],
    t,
    isAnyLicenceAuthorityValid,
    hasWeaponLicenceModule,
    showOnMap,
    markedText,
  }: IRegisteredFirearmsTableColumns,
  selectedLanguage: string | undefined,
  dateFormat: string,
): ITableColumns[] => {
  const customFieldColumns: ITableColumns[] = customFields
    .filter((customField) => customField.visibleInSearchResults)
    .map((customField) => ({
      id: customField.uniqueId,
      key: `customField.${customField.name}`,
      title: getColumnTitleFromCustomField(customField, selectedLanguage),
      isSortable: false,
      render: (data, props) => {
        if (props?.customFields?.length) {
          const cf = props.customFields.find(
            (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
          );
          if (cf) {
            if (cf.customFieldType === ECustomFieldType.BOOL && t) {
              if (cf.value === null) {
                return '-';
              }
              return cf.value
                ? t('customFields.value.yes.label')
                : t('customFields.value.no.label');
            }
            if (cf.customFieldType === ECustomFieldType.DATE) {
              return formatDate(cf.value, dateFormat);
            }
            return cf.value || '-';
          }
        }
        return '-';
      },
    }));
  let infoColumn: ITableColumns[] = [];
  infoColumn = [
    {
      id: '4eaea4ee-d36f-400b-895b-231c81f091cf',
      key: 'info',
      width: '78px',
      isSortable: false,
      title: 'registeredFirearms.accordion.info.column',
      render: (_data, props) => {
        const textToTooltip = [];
        if (hasWeaponLicenceModule) {
          if (props.showNoActiveLicenceWarning) {
            textToTooltip.push(t('registeredFirearms.showNoActiveLicenceWarning.text'));
          }
          if (props.showNoOwnerActiveLicenceWarning) {
            textToTooltip.push(t('registeredFirearms.showNoOwnerActiveLicenceWarning.text'));
          }
          if (props.showNoKeeperActiveLicenceWarning) {
            textToTooltip.push(t('registeredFirearms.showNoKeeperActiveLicenceWarning.text'));
          }
          if (props.showExpiredLinkedWarning) {
            textToTooltip.push(t('registeredFirearms.showExpiredLinkedWarning.text'));
          }
        }
        if (props.showInactiveOwnerOrKeeperWarning) {
          textToTooltip.push(t('registeredFirearms.showInactiveOwnerOrKeeperWarning.text'));
        }

        return (
          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
            {!!textToTooltip.length && (
              <Tooltip titleArray={textToTooltip}>
                <Icon type={EIconTypes.infoWarning} fontSize={20} />
              </Tooltip>
            )}
            {props.groupName && (
              <Tooltip
                title={t('registeredFirearms.groupName.text').replace(
                  ':name',
                  props.groupName,
                )}
              >
                <Icon type={EIconTypes.group} fontSize={20} />
              </Tooltip>
            )}
            {props.markings && (
              <TooltipInfoCell
                iconType={EIconTypes.markings}
                title={t('registeredFirearms.firearmsDetails.firearmMarkings.section.name')}
                markedText={markedText}
                rows={[
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.importMarking.label',
                    ),
                    value: props.markings[EMarkingTypes.IMPORT],
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.appliedMarking.label',
                    ),
                    value: props.markings[EMarkingTypes.APPLIED],
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.deactivationMarking.label',
                    ),
                    value: props.markings[EMarkingTypes.DEACTIVATION],
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.exportMarking.label',
                    ),
                    value: props.markings[EMarkingTypes.EXPORT],
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.surplusSaleMarking.label',
                    ),
                    value: props.markings[EMarkingTypes.SURPLUS_SALE],
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.yearOfTransfer.label',
                    ),
                    value: props?.yearOfTransfer,
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.confiscationMarking.label',
                    ),
                    value: props.markings[EMarkingTypes.CONFISCATION],
                  },
                  {
                    label: t(
                      'registeredFirearms.firearmsDetails.firearmMarkings.yearOfConfiscation.label',
                    ),
                    value: props?.yearOfConfiscation,
                  },
                ]}
              />
            )}
          </div>
        );
      },
    },
  ];

  return [
    ...infoColumn,
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f610',
      key: 'fireCycleId',
      title: 'FireCycle ID',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f510',
      key: 'serialNumber',
      title: 'registeredFirearms.accordion.serialNumber.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f517',
      key: 'state',
      isCenterTableHead: true,
      title: 'registeredFirearms.accordion.state.column',
      align: 'center',
      width: '200px',
      render: (_data, props) => (
        <Chips text={props.state} variant={renderStateFieldColor(props)} fullWidth />
      ),
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f699',
      key: 'exhibitNumber',
      title: 'accessoriesTable.accordion.exhibitNumber.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f711',
      key: 'make',
      title: 'registeredFirearms.accordion.make.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
      key: 'model',
      title: 'registeredFirearms.accordion.model.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f518',
      key: 'manufacturer',
      title: 'registeredFirearms.accordion.manufacturer.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f514',
      key: 'caliber',
      title: 'registeredFirearms.accordion.calibre.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
      key: 'owner',
      title: 'registeredFirearms.accordion.owner.column',
    },
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
      key: 'keeper',
      title: 'registeredFirearms.accordion.keeper.column',
    },

    ...customFieldColumns,
    {
      id: 'fe7a7688-1583-4233-8319-dd5c8973cc44',
      key: '',
      title: 'registeredFirearms.accordion.actions.column',
      isSortable: false,
      width: '110px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <>
          <Link to={`${ERouteLinks.ViewRegisteredFirearm.replace(':id', props.id)}`}>
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.view}
              sx={{ marginRight: '2px' }}
              tableButton
            />
          </Link>

          {isAnyLicenceAuthorityValid(props.licenceAuthorityIds) &&
            customFields?.length > 0 && (
              <Button
                variant={EButtonVariants.outlined}
                icon={EIconTypes.edit}
                onClick={() => handleEdit(props)}
                permission={[EPermission.MODIFY_FIREARM]}
                tableButton
              />
            )}
          {props.isLatLong && (
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.map}
              onClick={() => {
                showOnMap(props.id);
              }}
              tableButton
            />
          )}
        </>
      ),
    },
  ];
};

export enum ESearchFRTContextType {
  LegalEntities = 'LegalEntities',
  Firearms = 'Firearms',
}

const presetKey = 'preset';

export const hasQuerySetPresetValues = (query: { [k: string]: any }) => {
  const keys = Object.keys(query);
  if (keys.includes(presetKey) && query[presetKey].length) {
    let result = false;
    keys.forEach((key) => {
      const value = query[key];
      if (
        key !== presetKey &&
        ((!Array.isArray(value) && value) || (Array.isArray(value) && value.length))
      ) {
        result = true;
      }
    });
    return result;
  }
  return true;
};

export const mapFRTToDataSource = (
  frt: ISingleFRT[],
  make: IDictionary[] | null,
  model: IDictionary[] | null,
  manufacturer: IDictionary[] | null,
  firearmAction: IDictionary[] | null,
  firearmType: IDictionary[] | null,
  country: IDictionary[] | null,
): ITableDataSource[] =>
  frt.map((firearm) => ({
    id: firearm.id,
    make: findInDictionary(firearm.makeUniqueId, make),
    makeUniqueId: firearm.makeUniqueId,
    model: findInDictionary(firearm.modelUniqueId, model),
    modelUniqueId: firearm.modelUniqueId,
    manufacturer: findInDictionary(firearm.manufacturerUniqueId, manufacturer),
    manufacturerUniqueId: firearm.manufacturerUniqueId,
    action: findInDictionary(firearm.actionUniqueId, firearmAction),
    actionUniqueId: firearm.actionUniqueId,
    type: findInDictionary(firearm.typeUniqueId, firearmType),
    typeUniqueId: firearm.typeUniqueId,
    country: findInDictionary(firearm.countryUniqueId, country),
    countryUniqueId: firearm.countryUniqueId,
    frtId: firearm.frtId,
  }));

interface IFRTTableColumns {
  handleEdit?: (arg: string) => void;
  handleSelect?: (arg: ISingleFRT) => void;
  hasPermissionManageFRT?: boolean | null;
}

export const FRTTableColumns = (
  { handleEdit, handleSelect, hasPermissionManageFRT }: IFRTTableColumns,
  context: ESearchFRTContextType,
): ITableColumns[] => [
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f511',
    key: 'make',
    title: 'firearmsReferenceTable.accordion.make.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'model',
    title: 'firearmsReferenceTable.accordion.model.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'manufacturer',
    title: 'firearmsReferenceTable.accordion.manufacturer.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f514',
    key: 'action',
    title: 'firearmsReferenceTable.accordion.firearmAction.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f515',
    key: 'type',
    title: 'firearmsReferenceTable.accordion.type.column',
  },
  {
    id: 'f0504ca5-6b32-482d-b369-304a6f32f516',
    key: 'country',
    title: 'firearmsReferenceTable.accordion.country.column',
  },

  {
    id: 'fe7a7688-1583-4233-8319-dd5c8973cc44',
    key: '',
    title: 'firearmsReferenceTable.accordion.action.column',
    isSortable: false,
    width: '80px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <div>
        {context === ESearchFRTContextType.Firearms ? (
          <>
            <Link to={ERouteLinks.FRTDetails.replace(':frtId', props.id)}>
              <Button
                id={`firearms_reference_table_view-button-${props.id}`}
                variant={EButtonVariants.outlined}
                icon={EIconTypes.view}
                sx={{ marginRight: '2px' }}
                tableButton
              />
            </Link>

            {isFRTLocal(props.id) && hasPermissionManageFRT && (
              <Button
                id={`firearms_reference_table_edit-button-${props.id}`}
                variant={EButtonVariants.outlined}
                icon={EIconTypes.edit}
                onClick={() => handleEdit && handleEdit(props.id)}
                tableButton
              />
            )}
          </>
        ) : (
          <Button
            id={`firearms_reference_table_checkmark-button-${props.id}`}
            variant={EButtonVariants.outlined}
            icon={EIconTypes.checkmark}
            onClick={() => handleSelect && handleSelect(props)}
            tableButton
          />
        )}
      </div>
    ),
  },
];

export const firearmLicencesTableColumns = ({
  handleView,
  dateFormat,
  weaponLicenceStatus,
}: IRegisteredFirearmLicencesTableColumns): ITableColumns[] => [
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'licenceNumber',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.licenceNumber.column',
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f510',
    key: 'licenceName',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.licenceName.column',
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f511',
    key: 'issueDate',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.issueDate.column',
    dateFormat,
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f514',
    key: 'startDate',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.startDate.column',
    dateFormat,
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f515',
    key: 'endDate',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.endDate.column',
    dateFormat,
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f518',
    key: 'status',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.status.column',
    render: (_data, props) => (
      <Chips
        text={getDictionaryValue(props.statusUniqueId, weaponLicenceStatus || [])}
        variant={getLicenceStatusChipVariant(props.statusUniqueId)}
        fullWidth
      />
    ),
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'ownerName',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.licenceOwner.column',
    render: (_data, props) => (
      <StyledLegalLink
        legalId={props.ownerUniqueId}
        legalType={getLegalEntityType(props.legalType)}
        labelText={props.owner}
        legalVisibilityUniqueId={props.ownerVisibilityUniqueId}
        legalPrefixGroupUniqueId={props.ownerPrefixGroupUniqueId}
        legalOrganisationCategoryUniqueId={props.ownerOrganisationCategoryUniqueId}
      />
    ),
  },
  {
    id: 'ae7a7688-1583-4233-8319-dd5c8973cc44',
    key: '',
    title: 'registeredFirearms.firearmsDetails.licences.accordion.action.column',
    isSortable: false,
    align: 'center',
    width: '80px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <Button
        variant={EButtonVariants.outlined}
        icon={EIconTypes.view}
        onClick={() => handleView(props.id, props.legalType)}
        sx={{ marginRight: '2px' }}
        tableButton
      />
    ),
  },
];

export const mapFirearmLicenceToDataSource = (
  items: IRegisteredFirearmLicence[],
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.licenceUniqueId,
    licenceNumber: item.licenceNumber,
    licenceName: item.licenceName,
    issueDate: item.issueDate,
    startDate: item.startDate,
    endDate: item.endDate,
    statusUniqueId: item.statusUniqueId,
    legalType: item.ownerLegalType,
    owner: item.ownerName,
    ownerUniqueId: item.ownerUniqueId,
    ownerPrefixGroupUniqueId: item.ownerPrefixGroupUniqueId,
    ownerOrganisationCategoryUniqueId: item.ownerOrganisationCategoryUniqueId,
    ownerVisibilityUniqueId: item.ownerVisibilityUniqueId,
  }));

export const firearmTransactionsTableColumns = ({
  discardableFirearmTransactionId,
  handleView,
  handleDelete,
  showDetails,
  longDateFormat,
}: IRegisteredFirearmTransactionsTableColumns): ITableColumns[] => [
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f512',
    key: 'transactionType',
    title: 'registeredFirearms.firearmsDetails.transactions.accordion.transactionType.column',
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f510',
    key: 'date',
    title:
      'registeredFirearms.firearmsDetails.transactions.accordion.transactionDateAndTime.column',
    dateFormat: longDateFormat,
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f511',
    key: 'createdDate',
    title: 'registeredFirearms.firearmsDetails.transactions.accordion.createdDate.column',
    dateFormat: longDateFormat,
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f513',
    key: 'owner',
    title: 'registeredFirearms.firearmsDetails.transactions.accordion.owner.column',
  },
  {
    id: 'a0504ca5-6b32-482d-b369-304a6f32f514',
    key: 'keeper',
    title: 'registeredFirearms.firearmsDetails.transactions.accordion.keeper.column',
  },
  {
    id: 'ae7a7688-1583-4233-8319-dd5c8973cc44',
    key: '',
    title: 'registeredFirearms.firearmsDetails.transactions.accordion.action.column',
    isSortable: false,
    align: 'center',
    width: '80px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <>
        {showDetails(props) && handleView && (
          <Link to={ERouteLinks.ViewTransaction.replace(':id', props.id)}>
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.view}
              sx={{ marginRight: '2px' }}
              tableButton
            />
          </Link>
        )}

        {props.id === discardableFirearmTransactionId && handleDelete && (
          <Button
            variant={EButtonVariants.outlined}
            icon={EIconTypes.delete}
            onClick={() => handleDelete(props.id)}
            tableButton
          />
        )}
      </>
    ),
  },
];

export const mapFirearmTransactionToDataSource = (
  items: IRegisteredFirearmTransaction[],
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    date: item.date,
    createdDate: item.createdDate,
    transactionType: item.transactionType,
    keeper: item.keeper,
    owner: item.owner,
    keeperId: item.keeperId,
    ownerId: item.ownerId,
    detailsVisible: item.detailsVisible,
  }));

export const firearmAttachmentsTableColumns = ({
  firearmId,
  handleDelete,
  showDelete = true,
  handleShowTransaction,
  longDateFormat,
}: IRegisteredFirearmAttachmentsTableColumns): ITableColumns[] => [
  {
    id: 'a0504ca5-6b33-482d-b369-304a6f32f510',
    key: 'createdDate',
    title: 'registeredFirearms.firearmsDetails.attachments.accordion.dateAndTime.column',
    dateFormat: longDateFormat,
  },
  {
    id: 'a0504ca5-6b33-482d-b369-304a6f32f511',
    key: 'name',
    title: 'registeredFirearms.firearmsDetails.attachments.accordion.attachmentName.column',
  },
  {
    id: 'a0504ca5-6b33-482d-b369-304a6f32f512',
    key: 'size',
    title: 'registeredFirearms.firearmsDetails.attachments.accordion.attachmentSize.column',
  },
  {
    id: 'a0504ca5-6b33-482d-b369-304a6f32f513',
    key: 'transactionType',
    title: 'registeredFirearms.firearmsDetails.attachments.accordion.transactionType.column',
    isSortable: false,
  },
  {
    id: 'a0504ca5-6b33-482d-b369-304a6f32f515',
    key: '',
    title: 'registeredFirearms.firearmsDetails.attachments.accordion.action.column',
    isSortable: false,
    align: 'center',
    width: '80px',
    isCenterTableHead: true,
    render: (_data, props) => (
      <>
        <Button
          variant={EButtonVariants.outlined}
          icon={EIconTypes.download}
          onClick={() => downloadAttachment(firearmId, props.id, props.name, props.type)}
          sx={{ marginRight: '2px' }}
          tableButton
        />
        {props.transactionId && (
          <Button
            variant={EButtonVariants.outlined}
            icon={EIconTypes.view}
            onClick={() => handleShowTransaction(props.id)}
            sx={{ marginRight: '2px' }}
            tableButton
          />
        )}
        {showDelete &&
          !props.transactionId &&
          props.type.toLowerCase() !== EFileType.XLSX &&
          !props.isReport && (
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.delete}
              onClick={() => handleDelete(props)}
              sx={{ marginRight: '2px' }}
              tableButton
            />
          )}
        {!props.transactionId &&
          props.type.toLowerCase() !== EFileType.XLSX &&
          props.isReport && (
            <Button
              variant={EButtonVariants.outlined}
              icon={EIconTypes.view}
              onClick={() =>
                downloadAttachment(firearmId, props.id, props.name, props.type, true)
              }
              sx={{ marginRight: '2px' }}
              tableButton
            />
          )}
      </>
    ),
  },
];

export const mapFirearmAttachmentToDataSource = (
  items: IRegisteredFirearmAttachment[],
  transactionType: IDictionary[] | null,
  dateFormat: string,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    name: item.name,
    type: item.type,
    createdDate: item.createdDate,
    transactionType: `${
      item.transactionTypeUniqueId
        ? parseDictionary(transactionType, 'name')[item.transactionTypeUniqueId]
        : ''
    } ${formatDate(item.transactionTime, dateFormat)}`,
    transactionId: item.transactionId,
    size: Math.trunc(Number(item.size) / 1024),
    isReport: item.report,
  }));

export const isEmptyObject = (object?: { [key: string]: any }) => {
  if (object) {
    const objectRef = object;
    const reducedObject = Object.keys(objectRef)
      .filter(
        (k) => objectRef[k] !== undefined && objectRef[k] !== null && objectRef[k].length,
      )
      .reduce((a, k) => ({ ...a, [k]: objectRef[k] }), {});
    return !Object.keys(reducedObject).length;
  }
  return true;
};
